import { useCallback, useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { ArrowBack } from '@mui/icons-material';
import withAuth from 'hocs/withAuth/withAuth';
import {
  MediaPermissionsError,
  MediaPermissionsErrorType,
  requestMediaPermissions,
} from 'mic-check';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import { getExports } from 'services/env';

import { CareMatrixModule } from 'components/CareMatrixModule';

import { MeetProps } from './types';

import * as S from './styles';

export function Meet({ hideMenu }: MeetProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModule, isModuleOpen } = useCareMatrix();
  const navigate = useNavigate();

  const patientCognitoId = searchParams.get('patientCognitoId');
  const callUrl = searchParams.get('callUrl');

  const handleOpenModule = useCallback(() => {
    /**
     * We are usign the ! because we are validating if the callUrl is present
     *  on the useEffect. Because of that, this variable should never be undefined
     */
    openModule(patientCognitoId!);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientCognitoId]);

  useEffect(() => {
    setSearchParams(urlParams => {
      urlParams.set('hideMenu', String(hideMenu));

      return urlParams;
    });
  }, [hideMenu, searchParams, setSearchParams]);

  useEffect(() => {
    requestMediaPermissions()
      .then(() => {
        datadogLogs.logger.info(
          `[Call][requestMediaPermissions] The specialist has granted access to the camera and microphone.`,
          {
            patientCognitoId,
          },
        );
      })
      .catch((err: MediaPermissionsError) => {
        const { type, name, message } = err;
        if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
          // browser does not have permission to access camera or microphone

          datadogLogs.logger.warn(
            `[Call][requestMediaPermissions] The specialists browser does not have permission to access the camera and microphone.`,
          );
        } else if (type === MediaPermissionsErrorType.UserPermissionDenied) {
          // user didn't allow app to access camera or microphone

          datadogLogs.logger.warn(
            `[Call][requestMediaPermissions] The specialists does not have granted permission to access the camera and microphone.`,
          );
        } else if (
          type === MediaPermissionsErrorType.CouldNotStartVideoSource
        ) {
          // camera is in use by another application (Zoom, Skype) or browser tab (Google Meet, Messenger Video)
          // (mostly Windows specific problem)

          datadogLogs.logger.warn(
            `[Call][requestMediaPermissions] The specialists camera/microphone is in use by another application.`,
          );
        } else {
          // not all error types are handled by this library
          datadogLogs.logger.error(
            `[Call][requestMediaPermissions] An error occurred while requesting media permissions.`,
            {
              error: {
                name,
                message,
              },
            },
          );
        }
      });
  }, [patientCognitoId]);

  useEffect(() => {
    if (!patientCognitoId) {
      datadogLogs.logger.error(
        `[Call] The specialist tried to access the call but no patientCognitoId was provided in the URL.`,
        {
          searchParams: searchParams.toString,
        },
      );

      navigate('/care-provider/agenda/calendar');
    }

    if (!callUrl) {
      datadogLogs.logger.error(
        `[Call] The specialist tried to access the call but no callUrl was provided in the URL.`,
        {
          searchParams: searchParams.toString,
        },
      );

      navigate('/care-provider/agenda/calendar');
    }
  }, [callUrl, navigate, patientCognitoId, searchParams.toString]);

  useEffect(() => {
    if (window.innerWidth >= SCREEN_MIN_WIDTH) {
      handleOpenModule();
    }
  }, [handleOpenModule]);

  useEffect(() => {
    async function checkAllowedHosts() {
      const { allowedHosts } = await getExports();

      const hosts = allowedHosts.split(',');
      const currentHost = new URL(callUrl!).hostname;

      if (!hosts.includes(currentHost)) {
        datadogLogs.logger.error(
          `[Call] The specialist tried to access the call but the callUrl host is not allowed.`,
          {
            allowedHosts,
            currentHost,
          },
        );

        navigate('/care-provider/agenda/calendar');
      }
    }

    checkAllowedHosts();
  }, [callUrl, navigate]);

  return (
    <S.Wrapper>
      <S.VideoContainer>
        {}
        <iframe
          src={callUrl!}
          title="Video-Call"
          allow="camera; microphone; display-capture;"
        />
      </S.VideoContainer>
      <S.ModuleWrapper isExpanded={isModuleOpen}>
        {!isModuleOpen && (
          <S.ExpandModuleButton onClick={handleOpenModule}>
            <ArrowBack />
          </S.ExpandModuleButton>
        )}

        {isModuleOpen && <CareMatrixModule />}
      </S.ModuleWrapper>
    </S.Wrapper>
  );
}

export default withAuth(Meet);
